/* ===================================================================
   PRODUCT GALLERY — vanilla JS, no dependencies, 60fps transform slider
   Features: swipe, mouse-drag, infinite loop, keyboard, wheel, thumbs,
   lazy load + preload, fullscreen lightbox, pinch/double-tap/hover zoom.
   =================================================================== */
(function () {
  'use strict';

  function initGallery(root) {
    var viewport = root.querySelector('.pg-viewport');
    var track = root.querySelector('.pg-track');
    var slides = Array.prototype.slice.call(root.querySelectorAll('.pg-slide'));
    if (!viewport || !track || slides.length === 0) return;

    var realCount = slides.length;
    var loopEnabled = realCount > 1;
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('.pg-thumb'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('.pg-dot'));
    var counter = root.querySelector('.pg-counter');
    var prevBtn = root.querySelector('.pg-prev');
    var nextBtn = root.querySelector('.pg-next');
    var expandBtn = root.querySelector('.pg-expand');
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* ---- build infinite-loop clones ---- */
    var offset = 0;
    if (loopEnabled) {
      var firstClone = slides[0].cloneNode(true);
      var lastClone = slides[realCount - 1].cloneNode(true);
      firstClone.setAttribute('aria-hidden', 'true');
      lastClone.setAttribute('aria-hidden', 'true');
      firstClone.querySelectorAll('img').forEach(function (img) { img.removeAttribute('loading'); });
      lastClone.querySelectorAll('img').forEach(function (img) { img.removeAttribute('loading'); });
      track.appendChild(firstClone);
      track.insertBefore(lastClone, slides[0]);
      offset = 1; // index shift because of prepended clone
    }

    var allSlides = Array.prototype.slice.call(root.querySelectorAll('.pg-slide'));
    var index = 0; // logical index into real slides (0-based)
    var current = offset; // index into allSlides

    function setTrackX(px, animate) {
      track.classList.toggle('is-animating', !!animate && !reduceMotion);
      track.style.transform = 'translate3d(' + px + 'px,0,0)';
    }

    function widthPx() { return viewport.getBoundingClientRect().width; }

    function goTo(newCurrent, animate) {
      current = newCurrent;
      setTrackX(-current * widthPx(), animate !== false);
      var logical = current - offset;
      if (logical < 0) logical = realCount - 1;
      if (logical >= realCount) logical = 0;
      index = logical;
      updateUI();
      preloadNeighbors();
    }

    function next() { goTo(current + 1, true); }
    function prev() { goTo(current - 1, true); }

    function updateUI() {
      thumbs.forEach(function (t, i) { t.classList.toggle('active', i === index); });
      dots.forEach(function (d, i) { d.classList.toggle('active', i === index); });
      if (counter) counter.textContent = (index + 1) + ' / ' + realCount;
      if (thumbs[index] && thumbs[index].scrollIntoView) {
        var thumbsWrap = root.querySelector('.pg-thumbs');
        if (thumbsWrap) {
          var t = thumbs[index];
          var target = t.offsetLeft - (thumbsWrap.clientWidth - t.clientWidth) / 2;
          thumbsWrap.scrollTo({ left: target, behavior: reduceMotion ? 'auto' : 'smooth' });
        }
      }
    }

    /* ---- snap back to real slide instantly after hitting a clone ---- */
    track.addEventListener('transitionend', function () {
      if (!loopEnabled) return;
      if (current === allSlides.length - 1) { // sitting on trailing clone (clone of first)
        goTo(offset, false);
      } else if (current === 0) { // sitting on leading clone (clone of last)
        goTo(allSlides.length - 2, false);
      }
    });

    /* ---- lazy load + preload neighbors ---- */
    function loadImg(img) {
      if (!img || img.dataset.loaded === '1') return;
      if (img.dataset.src) img.src = img.dataset.src;
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      img.dataset.loaded = '1';
    }
    function preloadNeighbors() {
      [current - 1, current, current + 1].forEach(function (i) {
        var s = allSlides[(i + allSlides.length) % allSlides.length];
        if (s) loadImg(s.querySelector('img'));
      });
    }
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            loadImg(entry.target.querySelector('img'));
          }
        });
      }, { rootMargin: '600px' });
      allSlides.forEach(function (s) { io.observe(s); });
    } else {
      allSlides.forEach(function (s) { loadImg(s.querySelector('img')); });
    }

    /* ---- resize handling ---- */
    var resizeRAF;
    window.addEventListener('resize', function () {
      cancelAnimationFrame(resizeRAF);
      resizeRAF = requestAnimationFrame(function () { goTo(current, false); });
    });

    /* ---- thumbnails / dots click ---- */
    thumbs.forEach(function (t, i) {
      t.addEventListener('click', function () { goTo(offset + i, true); });
    });
    dots.forEach(function (d, i) {
      d.addEventListener('click', function () { goTo(offset + i, true); });
    });
    if (prevBtn) prevBtn.addEventListener('click', prev);
    if (nextBtn) nextBtn.addEventListener('click', next);

    /* ---- keyboard arrows ---- */
    root.setAttribute('tabindex', '0');
    root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { prev(); e.preventDefault(); }
      if (e.key === 'ArrowRight') { next(); e.preventDefault(); }
    });

    /* ---- mouse wheel (horizontal intent only, so page scroll still works) ---- */
    var wheelLock = false;
    viewport.addEventListener('wheel', function (e) {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return; // vertical scroll = ignore
      e.preventDefault();
      if (wheelLock) return;
      wheelLock = true;
      if (e.deltaX > 0) next(); else prev();
      setTimeout(function () { wheelLock = false; }, 350);
    }, { passive: false });

    /* ---- pointer drag / swipe with momentum ---- */
    var dragging = false, startX = 0, startTX = 0, lastX = 0, lastT = 0, velocity = 0;

    function currentTX() {
      var m = track.style.transform.match(/translate3d\(([-\d.]+)px/);
      return m ? parseFloat(m[1]) : -current * widthPx();
    }

    viewport.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      dragging = true;
      startX = e.clientX;
      startTX = -current * widthPx();
      lastX = e.clientX;
      lastT = Date.now();
      velocity = 0;
      track.classList.remove('is-animating');
      viewport.classList.add('is-dragging');
      viewport.setPointerCapture && viewport.setPointerCapture(e.pointerId);
    });

    viewport.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - startX;
      var now = Date.now();
      var dt = now - lastT || 16;
      velocity = (e.clientX - lastX) / dt;
      lastX = e.clientX; lastT = now;
      setTrackX(startTX + dx, false);
    });

    function endDrag(e) {
      if (!dragging) return;
      dragging = false;
      viewport.classList.remove('is-dragging');
      var w = widthPx();
      var dx = (e.clientX || lastX) - startX;
      var threshold = w * 0.18;
      var flick = Math.abs(velocity) > 0.5;
      if (dx < -threshold || (flick && velocity < 0)) {
        next();
      } else if (dx > threshold || (flick && velocity > 0)) {
        prev();
      } else {
        goTo(current, true);
      }
    }
    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);
    viewport.addEventListener('pointerleave', function (e) { if (dragging) endDrag(e); });

    /* prevent native image drag ghost */
    root.querySelectorAll('img').forEach(function (img) { img.setAttribute('draggable', 'false'); });

    /* ---- desktop hover-zoom (follows cursor) ---- */
    slides.concat(allSlides).forEach(function () {});
    allSlides.forEach(function (slide) {
      var img = slide.querySelector('img');
      if (!img) return;
      slide.addEventListener('mousemove', function (e) {
        if (!slide.classList.contains('pg-zoomable')) return;
        var r = slide.getBoundingClientRect();
        var x = ((e.clientX - r.left) / r.width) * 100;
        var y = ((e.clientY - r.top) / r.height) * 100;
        img.style.transformOrigin = x + '% ' + y + '%';
      });
      slide.addEventListener('mouseleave', function () {
        img.style.transformOrigin = 'center center';
      });
    });

    /* ---- double-tap to zoom (mobile, on main viewport) ---- */
    var lastTap = 0;
    viewport.addEventListener('touchend', function (e) {
      var now = Date.now();
      if (now - lastTap < 300) {
        var slide = allSlides[current];
        if (slide) {
          slide.classList.toggle('is-zoomed');
        }
        e.preventDefault();
      }
      lastTap = now;
    });

    /* init */
    goTo(offset, false);

    /* ---- lightbox ---- */
    var lightbox = root.parentElement.querySelector('.pg-lightbox') || document.querySelector('.pg-lightbox[data-gallery-for="' + root.id + '"]');
    if (expandBtn && lightbox) {
      setupLightbox(lightbox, root, allSlides, offset, realCount, function () { return index; }, goTo);
      expandBtn.addEventListener('click', function () { openLightbox(lightbox, index); });
      allSlides.forEach(function (slide, i) {
        slide.addEventListener('click', function (e) {
          if (slide.classList.contains('is-zoomed')) return;
          openLightbox(lightbox, index);
        });
      });
    }
  }

  var openLightboxFn = null;
  function openLightbox(lightbox, atIndex) {
    lightbox._api.open(atIndex);
  }

  function setupLightbox(lightbox, galleryRoot, allSlides, offset, realCount) {
    if (lightbox._api) return;
    var track = lightbox.querySelector('.pg-lightbox-track');
    var slides = Array.prototype.slice.call(lightbox.querySelectorAll('.pg-lightbox-slide'));
    var closeBtn = lightbox.querySelector('.pg-lightbox-close');
    var prevBtn = lightbox.querySelector('.pg-lightbox-arrow.pg-prev');
    var nextBtn = lightbox.querySelector('.pg-lightbox-arrow.pg-next');
    var counter = lightbox.querySelector('.pg-lightbox-counter');
    var thumbs = Array.prototype.slice.call(lightbox.querySelectorAll('.pg-thumb'));
    var stage = lightbox.querySelector('.pg-lightbox-stage');
    var i = 0;
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function loadImg(img) {
      if (!img || img.dataset.loaded === '1') return;
      if (img.dataset.src) img.src = img.dataset.src;
      img.dataset.loaded = '1';
    }

    function render(animate) {
      track.classList.toggle('is-animating', animate !== false && !reduceMotion);
      track.style.transform = 'translate3d(' + (-i * stage.clientWidth) + 'px,0,0)';
      thumbs.forEach(function (t, idx) { t.classList.toggle('active', idx === i); });
      if (counter) counter.textContent = (i + 1) + ' / ' + realCount;
      [i - 1, i, i + 1].forEach(function (n) {
        var s = slides[(n + slides.length) % slides.length];
        if (s) loadImg(s.querySelector('img'));
      });
    }

    function open(atIndex) {
      i = atIndex || 0;
      lightbox.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      render(false);
    }
    function close() {
      lightbox.classList.remove('is-open');
      document.body.style.overflow = '';
    }
    function next() { i = (i + 1) % realCount; render(true); }
    function prev() { i = (i - 1 + realCount) % realCount; render(true); }

    if (closeBtn) closeBtn.addEventListener('click', close);
    if (nextBtn) nextBtn.addEventListener('click', next);
    if (prevBtn) prevBtn.addEventListener('click', prev);
    lightbox.addEventListener('click', function (e) {
      if (e.target === lightbox) close();
    });
    document.addEventListener('keydown', function (e) {
      if (!lightbox.classList.contains('is-open')) return;
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    });
    thumbs.forEach(function (t, idx) {
      t.addEventListener('click', function () { i = idx; render(true); });
    });

    /* swipe on lightbox */
    var dragging = false, startX = 0, startTX = 0;
    stage.addEventListener('pointerdown', function (e) {
      dragging = true; startX = e.clientX;
      startTX = -i * stage.clientWidth;
      track.classList.remove('is-animating');
      stage.setPointerCapture && stage.setPointerCapture(e.pointerId);
    });
    stage.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      track.style.transform = 'translate3d(' + (startTX + (e.clientX - startX)) + 'px,0,0)';
    });
    function endDrag(e) {
      if (!dragging) return;
      dragging = false;
      var dx = (e.clientX || startX) - startX;
      if (dx < -60) next(); else if (dx > 60) prev(); else render(true);
    }
    stage.addEventListener('pointerup', endDrag);
    stage.addEventListener('pointercancel', endDrag);

    /* pinch to zoom */
    var pinchStartDist = 0, pinchScale = 1, activeImg = null;
    stage.addEventListener('touchstart', function (e) {
      if (e.touches.length === 2) {
        pinchStartDist = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
        activeImg = slides[i].querySelector('img');
      }
    }, { passive: true });
    stage.addEventListener('touchmove', function (e) {
      if (e.touches.length === 2 && activeImg) {
        var dist = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
        pinchScale = Math.min(Math.max(dist / pinchStartDist, 1), 3.5);
        activeImg.style.transform = 'scale(' + pinchScale + ')';
        e.preventDefault();
      }
    }, { passive: false });
    stage.addEventListener('touchend', function (e) {
      if (e.touches.length < 2 && activeImg) {
        if (pinchScale <= 1.05) { activeImg.style.transform = ''; }
        activeImg = null; pinchScale = 1;
      }
    });

    /* double tap zoom in lightbox */
    var lastTap = 0;
    stage.addEventListener('touchend', function () {
      var now = Date.now();
      if (now - lastTap < 300) {
        var img = slides[i].querySelector('img');
        if (img) {
          var zoomed = img.style.transform === 'scale(2)';
          img.style.transform = zoomed ? '' : 'scale(2)';
        }
      }
      lastTap = now;
    });

    window.addEventListener('resize', function () { if (lightbox.classList.contains('is-open')) render(false); });

    lightbox._api = { open: open, close: close };
  }

  function boot() {
    document.querySelectorAll('[data-product-gallery]').forEach(initGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
